import { LitElement, html, nothing } from "lit";
import { Chart, ArcElement, Tooltip, DoughnutController } from "chart.js";
import { packageEnergysourcesStyles } from "js/sharedStyles/packageEnergysources.js";

Chart.register(ArcElement, Tooltip, DoughnutController);

const SOURCE_COLORS = {
  erdgas: "#1F7AE0",
  regenerativ: "#00A894",
  nuklear: "#7D61FF",
  kohle: "#4A5568",
  "sonstige fossile energieträger": "#9E9E9E",
  "regenerativ eeg gefördert": "#FFB01F",
};
const DEFAULT_COLOR = "#9E9E9E";

const stripHtml = (raw) =>
  String(raw ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();

const sanitizeLabel = (label) => label.replace(/\bC0\s*2\b/gi, "CO2");

const parseNumber = (raw) => {
  const n = Number.parseFloat(stripHtml(raw).replace(/[%\s.]/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const normalize = (label) =>
  String(label ?? "")
    .toLowerCase()
    .replace(/₂/g, "2")
    .replace(/[^a-z0-9äöüß]+/g, " ")
    .trim();

const metaPriority = (label) => {
  const n = normalize(label);
  if (n.includes("radioaktiv") || n.includes("abfall")) return 0;
  if (n.includes("co2") || n.includes("co 2") || n.includes("emission")) return 1;
  return null;
};

const parseList = (list) => {
  const chartEntries = [];
  const metaEntries = [];

  for (const item of list ?? []) {
    const label = sanitizeLabel(stripHtml(item?.caption?.text));
    if (!label) continue;

    const valueText = stripHtml(item?.content?.text);
    const priority = metaPriority(label);

    if (priority !== null) {
      metaEntries.push({ label, valueText, priority });
      continue;
    }

    const value = parseNumber(valueText);
    if (value > 0) {
      chartEntries.push({ label, valueText, value, color: SOURCE_COLORS[normalize(label)] || DEFAULT_COLOR });
    }
  }

  metaEntries.sort((a, b) => a.priority - b.priority);
  return { chartEntries, metaEntries };
};

const chartData = (entries) => ({
  labels: entries.map((e) => e.label),
  datasets: [
    {
      data: entries.map((e) => e.value),
      backgroundColor: entries.map((e) => e.color),
      borderColor: "#FFFFFF",
      borderWidth: 2,
      borderRadius: 4,
      spacing: 2,
      hoverOffset: 6,
    },
  ],
});

const chartOptions = (onHover) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 700, easing: "easeInOutQuart" },
  transitions: { active: { animation: { duration: 250 } } },
  radius: "80%",
  cutout: "60%",
  layout: { padding: { top: 20, right: 30, bottom: 20, left: 30 } },
  interaction: { mode: "nearest", intersect: true },
  onHover,
  plugins: {
    tooltip: {
      displayColors: false,
      caretSize: 0,
      caretPadding: 12,
      xAlign: "center",
      yAlign: "bottom",
      padding: 10,
      callbacks: {
        label: (ctx) => `${Number.isFinite(ctx.parsed) ? String(ctx.parsed).replace(".", ",") : "0"} %`,
      },
    },
  },
});

class WJPackageEnergySources extends LitElement {
  static properties = {
    energySources: { type: Object },
    averageEnergyWithAmountMixs: { type: Array },
  };

  static styles = [packageEnergysourcesStyles];

  constructor() {
    super();
    this._charts = {};
    this._hovered = null;
    this._parts = {
      sources: { chartEntries: [], metaEntries: [] },
      emissions: { chartEntries: [], metaEntries: [] },
      average: { chartEntries: [], metaEntries: [] },
    };
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    Object.values(this._charts).forEach((c) => c?.destroy());
    this._charts = {};
  }

  willUpdate(changed) {
    if (!changed.has("energySources") && !changed.has("averageEnergyWithAmountMixs")) return;
    this._parts = {
      sources: parseList(this.energySources?.sources?.list),
      emissions: parseList(this.energySources?.emissions?.list),
      average: parseList(this.averageEnergyWithAmountMixs),
    };
  }

  updated(changed) {
    if (!changed.has("energySources") && !changed.has("averageEnergyWithAmountMixs")) return;
    this.syncChart("energySources", this._parts.sources.chartEntries);
    this.syncChart("averageMix", this._parts.average.chartEntries);
  }

  setHovered(value) {
    if (this._hovered?.chartKey === value?.chartKey && this._hovered?.itemIndex === value?.itemIndex) return;
    this._hovered = value;
    this.requestUpdate();
  }

  syncChart(key, entries) {
    const existing = this._charts[key];

    if (!entries.length) {
      existing?.destroy();
      delete this._charts[key];
      return;
    }

    if (existing) {
      existing.data = chartData(entries);
      existing.update();
      return;
    }

    const canvas = this.renderRoot.querySelector(`[data-chart-key="${key}"]`);
    if (!canvas) return;

    if (!canvas.offsetParent) {
      new IntersectionObserver((items, obs) => {
        if (!items[0].isIntersecting) return;
        obs.disconnect();
        this.syncChart(key, entries);
      }).observe(canvas);
      return;
    }

    this._charts[key] = new Chart(canvas, {
      type: "doughnut",
      data: chartData(entries),
      options: chartOptions((_e, active) => {
        const index = active?.[0]?.index ?? null;
        this.setHovered(index === null ? null : { chartKey: key, itemIndex: index });
      }),
    });
  }

  toggleLegendHighlight(chartKey, itemIndex, active) {
    const chart = this._charts[chartKey];
    if (!chart) return;

    if (active) {
      const el = chart.getDatasetMeta(0)?.data?.[itemIndex];
      const point = el?.tooltipPosition?.() || { x: chart.width / 2, y: chart.height / 2 };
      chart.setActiveElements([{ datasetIndex: 0, index: itemIndex }]);
      chart.tooltip.setActiveElements([{ datasetIndex: 0, index: itemIndex }], point);
    } else {
      chart.setActiveElements([]);
      chart.tooltip.setActiveElements([], { x: 0, y: 0 });
    }
    chart.update("none");
  }

  renderLabel(entry) {
    return html`${entry.label}${entry.valueText ? ` (${entry.valueText})` : ""}`;
  }

  renderChart(chartKey, { chartEntries, metaEntries }) {
    if (!chartEntries.length) return nothing;

    return html`
      <div class="c-energymix-chart layout layout--middle layout--small layout--nowrap">
        <div class="layout__item c-energymix-chart__canvas-wrap">
          <canvas data-chart-key="${chartKey}" aria-label="${chartKey} donut chart" role="img"></canvas>
        </div>
        <ul class="layout__item o-list-style--none m-0 p-0 c-energymix-chart__legend">
          ${chartEntries.map((entry, index) => {
            const isActive = this._hovered?.chartKey === chartKey && this._hovered?.itemIndex === index;
            return html`
              <li>
                <span
                  class="c-energymix-chart__legend-row ${isActive ? "c-energymix-chart__legend-row--active" : ""}"
                  @mouseenter=${() => {
                    this.setHovered({ chartKey, itemIndex: index });
                    this.toggleLegendHighlight(chartKey, index, true);
                  }}
                  @mouseleave=${() => {
                    this.setHovered(null);
                    this.toggleLegendHighlight(chartKey, index, false);
                  }}>
                  <span class="c-energymix-chart__legend-color" style="background-color: ${entry.color};"></span>
                  <span>${this.renderLabel(entry)}</span>
                </span>
              </li>
            `;
          })}
          ${metaEntries.map(
            (entry) => html`
              <li class="mt--">
                <span>${this.renderLabel(entry)}</span>
              </li>
            `
          )}
        </ul>
      </div>
    `;
  }

  render() {
    if (!this.energySources) return nothing;

    const { sources, emissions, average } = this._parts;
    const sourceParts = {
      chartEntries: sources.chartEntries,
      metaEntries: sources.metaEntries.length ? sources.metaEntries : emissions.metaEntries,
    };

    return html`
      <h5>${this.energySources.content?.text || "Energiequellen"}</h5>
      ${this.renderChart("energySources", sourceParts)}
      ${this.averageEnergyWithAmountMixs?.length
        ? html`
            <h5 class="mb- mt">Bundesdeutscher Durchschnitt zum Vergleich</h5>
            ${this.renderChart("averageMix", average)}
          `
        : nothing}
    `;
  }
}

const initWJPackageEnergySources = () => {
  window.customElements.define("wj-package-energysources", WJPackageEnergySources);
};

export { initWJPackageEnergySources };